import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { type z } from 'zod'

import { checkAuthenticatedTokenQuery } from '@/lib/queries/auth/business/authenticated/queries'
import { updatePasswordMutation } from '@/lib/queries/business/v1/user/password/update/mutations'

import { formSchema } from './constants/zod'

export const useResetPasswordPage = () => {
  const navigate = useNavigate()
  const { t: token } = useSearch({
    from: '/reset-password',
  })

  const { data: tokenData } = useSuspenseQuery(checkAuthenticatedTokenQuery(token))

  const { mutateAsync: updatePassword, isPending: isPendingResetPassword } = useMutation(updatePasswordMutation)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      passwordConfirm: '',
    },
  })

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      if (!token) {
        toast.warning('Invalid reset link. Please use the correct link from your email or request a new one.')
        return
      }

      await updatePassword(
        {
          password: values.password,
          token,
        },
        {
          onSuccess: () => {
            toast.success('Password updated successfully.')
            void navigate({
              to: '/sign-in',
              search: { email: tokenData.email },
            })
          },
          onError: () => {
            toast.error('Failed to update password.')
          },
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  )

  return {
    form,
    isPendingResetPassword,
    handleSubmit,
  }
}
